import { useMemo } from "react";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import { useLoyaltyBonus } from "../../BonusLoyalty/hooks/use-loyalty-bonus";

interface Rank {
  id: string;
  name: string;
  points: number;
  description: string;
  status: boolean;
  myRank: MyRank
}

interface MyRank {
  id: string;
  period: {
    start?: string;
    end?: string;
  };
  status?: string;
  user_data: {
    total_points: number;
    rank: {
      id: string;
      name: string;
      points_required: number;
    };
  };
}

export const useRanks = () => {
  const { totalPoints } = useLoyaltyBonus();
  const currentUserPoints = totalPoints || 0;

  const { getData } = useDataFetch();

  const { data, isLoading: ranksLoading, error } = getData(
    ["ranks"],
    "/ranks"
  );

  const { data: myRanksData, isLoading: myRanksLoading } = getData(
    ["my-ranks"],
    "/ranks/my-ranks"
  );

  const isLoading = ranksLoading || myRanksLoading;



  const sortedRanks: Rank[] = useMemo(() => {
    const ranks = (data?.data?.ranks || [])
      .slice()
      .sort((a: Rank, b: Rank) => a.points - b.points);

    const myRanks = myRanksData?.data || [];

    // Primer corte (el más antiguo) en que el usuario alcanzó cada rango. Un
    // corte con un rango superior también cuenta, así los rangos que se saltó
    // quedan con la fecha en que los superó. Las recalificaciones posteriores
    // no reemplazan ni la fecha ni el puntaje de ese primer logro.
    const cutTime = (item: any) =>
      new Date(item.period?.start ?? item.calculation_date ?? 0).getTime();

    const qualifyingCuts = myRanks
      .filter((item: any) => item.user_data?.rank?.qualifies !== false)
      .sort((a: any, b: any) => cutTime(a) - cutTime(b));

    return ranks.map((rank: Rank) => ({
      ...rank,
      myRank:
        qualifyingCuts.find(
          (item: any) =>
            (item.user_data?.rank?.points_required || 0) >= rank.points
        ) ?? null,
    }));
  }, [data, myRanksData]);

  const { currentRank, currentRankIndex } = useMemo(() => {
    let index = -1;

    sortedRanks.forEach((rank, i) => {
      if (rank.myRank) {
        index = i;
      }
    });

    return {
      currentRank:
        index >= 0
          ? sortedRanks[index].myRank
          : undefined,
      currentRankIndex: index,
    };
  }, [sortedRanks]);


  const nextRank =
    currentRankIndex >= 0
      ? sortedRanks[currentRankIndex + 1] || null
      : null;

  const currentRankPoints =
    currentRank?.user_data.rank.points_required || 0;

  const nextRankPoints = nextRank?.points || currentRankPoints;


  const progressToNextRank = useMemo(() => {
    if (!nextRank) return 100;

    const range = nextRankPoints - currentRankPoints;
    if (range <= 0) return 0;

    const progress = currentUserPoints - currentRankPoints;

    return Math.min(100, Math.max(0, (progress / range) * 100));
  }, [nextRank, currentUserPoints, currentRankPoints, nextRankPoints]);

  const { currentPointsRank, nextPointsRank } = useMemo(() => {
    let currentIndex = -1;

    sortedRanks.forEach((rank: Rank, index: number) => {
      if (currentUserPoints >= rank.points) {
        currentIndex = index;
      }
    });
    

    return {
      currentPointsRank:
        currentIndex >= 0 ? sortedRanks[currentIndex] : null,
      nextPointsRank:
        currentIndex >= 0
          ? sortedRanks[currentIndex + 1] || null
          : sortedRanks[0] || null,
    };
  }, [sortedRanks, currentUserPoints]);


  

  const pointsToNextRank = Math.max(
    0,
    nextPointsRank?.points - currentUserPoints
  );

  const pointsToNextRankHistory = nextRank
  ? Math.max(0, nextRank.points - currentUserPoints)
  : 0;




  return {
    ranks: sortedRanks,
    isLoading,
    error,
    currentRankIndex,
    currentUserPoints,
    currentRank,
    nextRank,
    pointsToNextRank,
    pointsToNextRankHistory,
    progressToNextRank,
    currentPointsRank,
    nextPointsRank,
    myRanks: myRanksData?.data || [],
  };
};